import { ChevronDown, ChevronRight } from 'react-feather';

const OrderPricingSummary = ({
	sections,
	toggleSection,
	model,
	installationSurface,
	roofStyle,
	roofPitch,
	csSize,
	basePrice,
	centerLegPrice,
	csSides,
	rightLeanToActive,
	rltSize,
	rightLeanPrice,
	rightLegPrice,
	leftLeanToActive,
	lltSize,
	leftLeanPrice,
	leftLegPrice,

	colorsData,
    csItems,
    rltItems,
    lltItems,
    frontLeanToActive,
    fltSize,
    fltItems,
    frontLeanPrice,
    frontLegPrice,
    backLeanToActive,
    bltSize,
    bltItems,
    backLeanPrice,
    backLegPrice,
    // Storage
    centerStorageData,
    leftLeanStorageData,
    rightLeanStorageData,
    frontLeanStorageData,
    backLeanStorageData,
	centerStoragePrice,
    leftStoragePrice,
    rightStoragePrice,
    frontStoragePrice,
    backStoragePrice,

    centerPrices,
    leftPrices,
    rightPrices,
    frontPrices,
    backPrices,
    priceSummary
}) => {
    // Helper to get price from a list by ID
    const getPrice = (priceList, id) => {
        if (!Array.isArray(priceList)) return 0;
        const item = priceList.find(p => p.id === id);
        return item?.price || 0;
    };

    // Helper to get item price (search by name or id match)
    const getItemPrice = (priceList, item) => {
        if (!Array.isArray(priceList) || !item) return 0;
        // Try to match by name first (common for windows/doors where name is size e.g. "30x30")
        let priceItem = priceList.find(p => p.name === item.name);
        // Fallback to ID if needed (though IDs in items list aren't always standard)
        if (!priceItem && item.type) priceItem = priceList.find(p => p.id === item.type);
        
        return priceItem?.price || 0;
    };

    // Helper to render items safely
    const renderItemsRows = (items, priceList) => {
        if (!items) return null;

        // Helper to render a single item row
        const renderRow = (item, index, side = '') => {
            let displayLabel = 'Item';
            if (item.categoryLabel) {
                displayLabel = item.categoryLabel;
            } else if (item.type && typeof item.type === 'string' && isNaN(parseInt(item.type))) {
                displayLabel = item.type;
            } else if (item.position?.wall) {
                 displayLabel = `${item.position.wall} Wall`;
            }

            const itemPrice = getItemPrice(priceList, item);

            return (
                <tr key={`${side}-${index}-${item.name}`} className="border-b border-slate-100 hover:bg-slate-50">
                    <td className={`p-3 text-slate-500 ${side ? 'pl-8' : 'pl-6'}`}>
                        <span className="flex items-center gap-2 capitalize">
                            {displayLabel}
                        </span>
                    </td>
                    <td className="p-3 font-medium text-slate-900">
                        <div className="flex justify-between items-center">
                            <span>{item.name}</span>
                            {itemPrice > 0 && <span className="text-emerald-600 font-bold ml-2">${itemPrice.toFixed(2)}</span>}
                        </div>
                    </td>
                </tr>
            );
        };

        // If items is grouped object (standard for new orders/orders with multiple sides)
        if (!Array.isArray(items) && typeof items === 'object') {
            const keys = ['front', 'back', 'left', 'right', 'roof', 'storage'];
            const rows = [];
            
            keys.forEach(side => {
                const sideItems = items[side];
                if (Array.isArray(sideItems) && sideItems.length > 0) {
                     // Filter valid items
                    const validItems = sideItems.filter(item => item && item.name);
                    
                    if (validItems.length > 0) {
                         // Add Header
                        rows.push(
                            <tr key={`header-${side}`} className="bg-slate-50 border-b border-slate-100">
                                <td colSpan="2" className="p-2 pl-4 font-bold text-slate-700 capitalize">
                                    {side} Wall
                                </td>
                            </tr>
                        );
                        // Add Items
                        validItems.forEach((item, index) => {
                            rows.push(renderRow(item, index, side));
                        });
                    }
                }
            });
            
            return rows;
        }
        
        // Fallback for flat arrays (legacy or simple lists)
        const itemsList = Array.isArray(items) ? items : [];
        return itemsList
            .filter(item => item && item.name)
            .map((item, index) => renderRow(item, index));
    };

	return (
		<div className="structure-details">
			<div className="structure-details-block">
				<div
					className="title-block"
					onClick={() => toggleSection('structure')}
				>
					<h2 className="title-h2">Structure Details</h2>
					<button>
						{sections.structure ? (
							<ChevronDown size={24} />
						) : (
							<ChevronRight size={24} />
						)}
					</button>
				</div>
				{sections.structure && (
					<div className="price-cont">
						<p className="price-subtitle">Structure Details</p>
						<div className="overflow-x-auto">
                            <table className="modern-table w-full">
                                <thead>
                                    <tr className="bg-slate-100 text-left">
                                        <th className="p-3 font-semibold text-slate-700">Feature</th>
                                        <th className="p-3 font-semibold text-slate-700">Detail / Price</th>
                                    </tr>
                                </thead>
                                <tbody className="text-sm">
                                    {/* --- GENERAL --- */}
                                    <tr className="bg-slate-50 border-b border-slate-200">
                                        <td colSpan="2" className="p-3 font-bold text-slate-800 uppercase tracking-wider">
                                            Base Configuration
                                        </td>
                                    </tr>
                                    <tr className="border-b border-slate-100 hover:bg-slate-50">
                                        <td className="p-3 text-slate-600">Model</td>
                                        <td className="p-3 font-medium text-slate-900">{model}</td>
                                    </tr>
                                    <tr className="border-b border-slate-100 hover:bg-slate-50">
                                        <td className="p-3 text-slate-600">Installation Surface</td>
                                        <td className="p-3 font-medium text-slate-900">{installationSurface}</td>
                                    </tr>
                                    <tr className="border-b border-slate-100 hover:bg-slate-50">
                                        <td className="p-3 text-slate-600">Roof Style</td>
                                        <td className="p-3 font-medium text-slate-900">{roofStyle}</td>
                                    </tr>
                                    <tr className="border-b border-slate-100 hover:bg-slate-50">
                                        <td className="p-3 text-slate-600">Roof Pitch</td>
                                        <td className="p-3 font-medium text-slate-900">{roofPitch}</td>
                                    </tr>

                                    {/* --- CENTER SECTION --- */}
                                    <tr className="bg-slate-50 border-b border-slate-200 border-t-2 border-t-slate-100">
                                        <td colSpan="2" className="p-3 font-bold text-slate-800 uppercase tracking-wider">
                                            Center Building
                                        </td>
                                    </tr>
                                    <tr className="border-b border-slate-100 hover:bg-slate-50">
                                        <td className="p-3 text-slate-600">Size</td>
                                        <td className="p-3 font-medium text-slate-900">
                                            <div className="flex justify-between items-center">
                                                <span>{csSize.width}'W x {csSize.length || csSize.lenght}'L</span>
                                                <span className="text-emerald-600 font-bold">${basePrice?.toFixed(2)}</span>
                                            </div>
                                        </td>
                                    </tr>
                                    <tr className="border-b border-slate-100 hover:bg-slate-50">
                                        <td className="p-3 text-slate-600">Height</td>
                                        <td className="p-3 font-medium text-slate-900">
                                             <div className="flex justify-between items-center">
                                                <span>{csSize.height}'H</span>
                                                <span className="text-emerald-600 font-bold">${centerLegPrice?.toFixed(2)}</span>
                                            </div>
                                        </td>
                                    </tr>
                                    <tr className="border-b border-slate-100 hover:bg-slate-50">
                                        <td className="p-3 text-slate-600">Front Wall</td>
                                        <td className="p-3 font-medium text-slate-900">
                                            <div className="flex justify-between items-center">
                                                <span>{csSides.frontWall}</span>
                                                {getPrice(centerPrices, 'frontWallPrice') > 0 && 
                                                    <span className="text-emerald-600 font-bold">${getPrice(centerPrices, 'frontWallPrice').toFixed(2)}</span>}
                                            </div>
                                        </td>
                                    </tr>
                                    <tr className="border-b border-slate-100 hover:bg-slate-50">
                                        <td className="p-3 text-slate-600">Back Wall</td>
                                        <td className="p-3 font-medium text-slate-900">
                                            <div className="flex justify-between items-center">
                                                <span>{csSides.backWall}</span>
                                                {getPrice(centerPrices, 'backWallPrice') > 0 && 
                                                    <span className="text-emerald-600 font-bold">${getPrice(centerPrices, 'backWallPrice').toFixed(2)}</span>}
                                            </div>
                                        </td>
                                    </tr>
                                    <tr className="border-b border-slate-100 hover:bg-slate-50">
                                        <td className="p-3 text-slate-600">Left Wall</td>
                                        <td className="p-3 font-medium text-slate-900">
                                            <div className="flex justify-between items-center">
                                                <span>{csSides.leftWall}</span>
                                                {getPrice(centerPrices, 'leftWallPrice') > 0 && 
                                                    <span className="text-emerald-600 font-bold">${getPrice(centerPrices, 'leftWallPrice').toFixed(2)}</span>}
                                            </div>
                                        </td>
                                    </tr>
                                    <tr className="border-b border-slate-100 hover:bg-slate-50">
                                        <td className="p-3 text-slate-600">Right Wall</td>
                                        <td className="p-3 font-medium text-slate-900">
                                            <div className="flex justify-between items-center">
                                                <span>{csSides.rightWall}</span>
                                                {getPrice(centerPrices, 'rightWallPrice') > 0 && 
                                                    <span className="text-emerald-600 font-bold">${getPrice(centerPrices, 'rightWallPrice').toFixed(2)}</span>}
                                            </div>
                                        </td>
                                    </tr>
                                    {renderItemsRows(csItems, centerPrices)}

                                    {/* --- RIGHT LEAN-TO --- */}
                                    {rightLeanToActive && (
                                        <>
                                            <tr className="bg-slate-50 border-b border-slate-200 border-t-2 border-t-slate-100">
                                                <td colSpan="2" className="p-3 font-bold text-slate-800 uppercase tracking-wider">
                                                    Right Lean-To
                                                </td>
                                            </tr>
                                            <tr className="border-b border-slate-100 hover:bg-slate-50">
                                                <td className="p-3 text-slate-600">Size</td>
                                                <td className="p-3 font-medium text-slate-900">
                                                    <div className="flex justify-between items-center">
                                                        <span>{rltSize.width}'W x {rltSize.length || rltSize.lenght}'L</span>
                                                        <span className="text-emerald-600 font-bold">${rightLeanPrice?.toFixed(2)}</span>
                                                    </div>
                                                </td>
                                            </tr>
                                            <tr className="border-b border-slate-100 hover:bg-slate-50">
                                                <td className="p-3 text-slate-600">Height</td>
                                                <td className="p-3 font-medium text-slate-900">
                                                    <div className="flex justify-between items-center">
                                                        <span>{rltSize.height}'H</span>
                                                        <span className="text-emerald-600 font-bold">${rightLegPrice?.toFixed(2)}</span>
                                                    </div>
                                                </td>
                                            </tr>
                                            {renderItemsRows(rltItems, rightPrices)}
                                        </>
                                    )}

                                    {/* --- LEFT LEAN-TO --- */}
                                    {leftLeanToActive && (
                                        <>
                                           <tr className="bg-slate-50 border-b border-slate-200 border-t-2 border-t-slate-100">
                                                <td colSpan="2" className="p-3 font-bold text-slate-800 uppercase tracking-wider">
                                                    Left Lean-To
                                                </td>
                                            </tr>
                                            <tr className="border-b border-slate-100 hover:bg-slate-50">
                                                <td className="p-3 text-slate-600">Size</td>
                                                <td className="p-3 font-medium text-slate-900">
                                                    <div className="flex justify-between items-center">
                                                        <span>{lltSize.width}'W x {lltSize.length || lltSize.lenght}'L</span>
                                                        <span className="text-emerald-600 font-bold">${leftLeanPrice?.toFixed(2)}</span>
                                                    </div>
                                                </td>
                                            </tr>
                                            <tr className="border-b border-slate-100 hover:bg-slate-50">
                                                <td className="p-3 text-slate-600">Height</td>
                                                <td className="p-3 font-medium text-slate-900">
                                                    <div className="flex justify-between items-center">
                                                        <span>{lltSize.height}'H</span>
                                                        <span className="text-emerald-600 font-bold">${leftLegPrice?.toFixed(2)}</span>
                                                    </div>
                                                </td>
                                            </tr>
                                            {renderItemsRows(lltItems, leftPrices)}
                                        </>
                                    )}

                                    {/* --- FRONT LEAN-TO --- */}
                                    {frontLeanToActive && (
                                        <>
                                           <tr className="bg-slate-50 border-b border-slate-200 border-t-2 border-t-slate-100">
                                                <td colSpan="2" className="p-3 font-bold text-slate-800 uppercase tracking-wider">
                                                    Front Lean-To
                                                </td>
                                            </tr>
                                            <tr className="border-b border-slate-100 hover:bg-slate-50">
                                                <td className="p-3 text-slate-600">Size</td>
                                                <td className="p-3 font-medium text-slate-900">
                                                    <div className="flex justify-between items-center">
                                                        <span>{fltSize.width}'W x {fltSize.length || fltSize.lenght}'L</span>
                                                        <span className="text-emerald-600 font-bold">${frontLeanPrice?.toFixed(2)}</span>
                                                    </div>
                                                </td>
                                            </tr>
                                            <tr className="border-b border-slate-100 hover:bg-slate-50">
                                                <td className="p-3 text-slate-600">Height</td>
                                                <td className="p-3 font-medium text-slate-900">
                                                    <div className="flex justify-between items-center">
                                                        <span>{fltSize.height}'H</span>
                                                        <span className="text-emerald-600 font-bold">${frontLegPrice?.toFixed(2)}</span>
                                                    </div>
                                                </td>
                                            </tr>
                                            {renderItemsRows(fltItems, frontPrices)}
                                        </>
                                    )}

                                    {/* --- BACK LEAN-TO --- */}
                                    {backLeanToActive && (
                                        <>
                                           <tr className="bg-slate-50 border-b border-slate-200 border-t-2 border-t-slate-100">
                                                <td colSpan="2" className="p-3 font-bold text-slate-800 uppercase tracking-wider">
                                                    Back Lean-To
                                                </td>
                                            </tr>
                                            <tr className="border-b border-slate-100 hover:bg-slate-50">
                                                <td className="p-3 text-slate-600">Size</td>
                                                <td className="p-3 font-medium text-slate-900">
                                                    <div className="flex justify-between items-center">
                                                        <span>{bltSize.width}'W x {bltSize.length || bltSize.lenght}'L</span>
                                                        <span className="text-emerald-600 font-bold">${backLeanPrice?.toFixed(2)}</span>
                                                    </div>
                                                </td>
                                            </tr>
                                            <tr className="border-b border-slate-100 hover:bg-slate-50">
                                                <td className="p-3 text-slate-600">Height</td>
                                                <td className="p-3 font-medium text-slate-900">
                                                    <div className="flex justify-between items-center">
                                                        <span>{bltSize.height}'H</span>
                                                        <span className="text-emerald-600 font-bold">${backLegPrice?.toFixed(2)}</span>
                                                    </div>
                                                </td>
                                            </tr>
                                            {renderItemsRows(bltItems, backPrices)}
                                        </>
                                    )}

                                    {/* --- STORAGE --- */}
                                    {/* Center Storage */}
                                    {centerStorageData?.length && centerStorageData.length !== 'none' && (
                                        <>
                                            <tr className="bg-slate-50 border-b border-slate-200 border-t-2 border-t-slate-100">
                                                <td colSpan="2" className="p-3 font-bold text-slate-800 uppercase tracking-wider">
                                                    Center Storage
                                                </td>
                                            </tr>
                                            <tr className="border-b border-slate-100 hover:bg-slate-50">
                                                <td className="p-3 text-slate-600">Length</td>
                                                <td className="p-3 font-medium text-slate-900">
                                                    <div className="flex justify-between items-center">
                                                        <span>{centerStorageData.length}'L ({centerStorageData.location})</span>
                                                        <span className="text-emerald-600 font-bold">${centerStoragePrice?.toFixed(2)}</span>
                                                    </div>
                                                </td>
                                            </tr>
                                        </>
                                    )}

                                    {/* Left Lean Storage */}
                                    {leftLeanStorageData?.length && leftLeanStorageData.length !== 'none' && (
                                        <>
                                            <tr className="bg-slate-50 border-b border-slate-200 border-t-2 border-t-slate-100">
                                                <td colSpan="2" className="p-3 font-bold text-slate-800 uppercase tracking-wider">
                                                    Left Lean-To Storage
                                                </td>
                                            </tr>
                                            <tr className="border-b border-slate-100 hover:bg-slate-50">
                                                <td className="p-3 text-slate-600">Length</td>
                                                <td className="p-3 font-medium text-slate-900">
                                                    <div className="flex justify-between items-center">
                                                        <span>{leftLeanStorageData.length}'L</span>
                                                        <span className="text-emerald-600 font-bold">${leftStoragePrice?.toFixed(2)}</span>
                                                    </div>
                                                </td>
                                            </tr>
                                        </>
                                    )}

                                    {/* Right Lean Storage */}
                                    {rightLeanStorageData?.length && rightLeanStorageData.length !== 'none' && (
                                        <>
                                            <tr className="bg-slate-50 border-b border-slate-200 border-t-2 border-t-slate-100">
                                                <td colSpan="2" className="p-3 font-bold text-slate-800 uppercase tracking-wider">
                                                    Right Lean-To Storage
                                                </td>
                                            </tr>
                                            <tr className="border-b border-slate-100 hover:bg-slate-50">
                                                <td className="p-3 text-slate-600">Length</td>
                                                <td className="p-3 font-medium text-slate-900">
                                                    <div className="flex justify-between items-center">
                                                        <span>{rightLeanStorageData.length}'L</span>
                                                        <span className="text-emerald-600 font-bold">${rightStoragePrice?.toFixed(2)}</span>
                                                    </div>
                                                </td>
                                            </tr>
                                        </>
                                    )}

                                    {/* Front Lean Storage */}
                                    {frontLeanStorageData?.length && frontLeanStorageData.length !== 'none' && (
                                        <>
                                            <tr className="bg-slate-50 border-b border-slate-200 border-t-2 border-t-slate-100">
                                                <td colSpan="2" className="p-3 font-bold text-slate-800 uppercase tracking-wider">
                                                    Front Lean-To Storage
                                                </td>
                                            </tr>
                                            <tr className="border-b border-slate-100 hover:bg-slate-50">
                                                <td className="p-3 text-slate-600">Length</td>
                                                <td className="p-3 font-medium text-slate-900">
                                                    <div className="flex justify-between items-center">
                                                        <span>{frontLeanStorageData.length}'L</span>
                                                        <span className="text-emerald-600 font-bold">${frontStoragePrice?.toFixed(2)}</span>
                                                    </div>
                                                </td>
                                            </tr>
                                        </>
                                    )}

                                    {/* Back Lean Storage */}
                                    {backLeanStorageData?.length && backLeanStorageData.length !== 'none' && (
                                        <>
                                            <tr className="bg-slate-50 border-b border-slate-200 border-t-2 border-t-slate-100">
                                                <td colSpan="2" className="p-3 font-bold text-slate-800 uppercase tracking-wider">
                                                    Back Lean-To Storage
                                                </td>
                                            </tr>
                                            <tr className="border-b border-slate-100 hover:bg-slate-50">
                                                <td className="p-3 text-slate-600">Length</td>
                                                <td className="p-3 font-medium text-slate-900">
                                                    <div className="flex justify-between items-center">
                                                        <span>{backLeanStorageData.length}'L</span>
                                                        <span className="text-emerald-600 font-bold">${backStoragePrice?.toFixed(2)}</span>
                                                    </div>
                                                </td>
                                            </tr>
                                        </>
                                    )}

                                    {/* --- COLORS --- */}
                                    <tr className="bg-slate-50 border-b border-slate-200 border-t-2 border-t-slate-100">
                                        <td colSpan="2" className="p-3 font-bold text-slate-800 uppercase tracking-wider">
                                            Colors
                                        </td>
                                    </tr>
                                    <tr className="border-b border-slate-100 hover:bg-slate-50">
                                        <td className="p-3 text-slate-600">Roof Color</td>
                                        <td className="p-3 font-medium text-slate-900">
                                            <div className="flex items-center gap-2">
                                                <div className="w-4 h-4 rounded-full border border-slate-300" 
                                                     style={{backgroundColor: colorsData.roof?.hex || colorsData.roof?.color || '#ccc'}}></div>
                                                {colorsData.roof?.name}
                                            </div>
                                        </td>
                                    </tr>
                                    <tr className="border-b border-slate-100 hover:bg-slate-50">
                                        <td className="p-3 text-slate-600">Trim Color</td>
                                        <td className="p-3 font-medium text-slate-900">
                                            <div className="flex items-center gap-2">
                                                <div className="w-4 h-4 rounded-full border border-slate-300" 
                                                     style={{backgroundColor: colorsData.trim?.hex || colorsData.trim?.color || '#ccc'}}></div>
                                                {colorsData.trim?.name}
                                            </div>
                                        </td>
                                    </tr>
                                    <tr className="border-b border-slate-100 hover:bg-slate-50">
                                        <td className="p-3 text-slate-600">Side Wall Color</td>
                                        <td className="p-3 font-medium text-slate-900">
                                             <div className="flex items-center gap-2">
                                                <div className="w-4 h-4 rounded-full border border-slate-300" 
                                                     style={{backgroundColor: colorsData.sideWalls?.hex || colorsData.sideWalls?.color || '#ccc'}}></div>
                                                {colorsData.sideWalls?.name}
                                            </div>
                                        </td>
                                    </tr>
                                    <tr className="border-b border-slate-100 hover:bg-slate-50">
                                        <td className="p-3 text-slate-600">End Wall Color</td>
                                        <td className="p-3 font-medium text-slate-900">
                                            <div className="flex items-center gap-2">
                                                <div className="w-4 h-4 rounded-full border border-slate-300" 
                                                     style={{backgroundColor: colorsData.endWalls?.hex || colorsData.endWalls?.color || '#ccc'}}></div>
                                                {colorsData.endWalls?.name}
                                            </div>
                                        </td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>


                        {/* Price Summary Section (Persisted) */}
                        {priceSummary && (
                            <div className="mt-8 border-t border-slate-200 pt-6">
                                <h4 className="text-sm font-bold text-slate-800 uppercase tracking-widest mb-4">Price Summary</h4>
                                <div className="bg-slate-50 rounded-xl p-5 border border-slate-100">
                                    <div className="space-y-3">
                                        <div className="flex justify-between items-center text-sm">
                                            <span className="text-slate-600 font-medium">Subtotal</span>
                                            <span className="font-bold text-slate-900">
                                                {(priceSummary.subTotal || 0).toLocaleString('en-US', { style: 'currency', currency: 'USD' })}
                                            </span>
                                        </div>
                                        
                                        <div className="flex justify-between items-center text-sm">
                                            <span className="text-slate-600 font-medium">
                                                Installation ({priceSummary.installationFee || 0}%)
                                            </span>
                                            <span className="font-bold text-slate-900">
                                                {(priceSummary.installationFeeAmount || 0).toLocaleString('en-US', { style: 'currency', currency: 'USD' })}
                                            </span>
                                        </div>

                                        <div className="flex justify-between items-center text-sm">
                                            <span className="text-slate-600 font-medium">
                                                Tax {priceSummary.taxRate ? `(${(priceSummary.taxRate * 100).toFixed(2)}%)` : ''}
                                            </span>
                                            <span className="font-bold text-slate-900">
                                                {(priceSummary.taxAmount || 0).toLocaleString('en-US', { style: 'currency', currency: 'USD' })}
                                            </span>
                                        </div>
                                        
                                        <div className="border-t border-slate-200 my-3 pt-3 flex justify-between items-center">
                                            <span className="text-base font-bold text-slate-800">Total</span>
                                            <span className="text-xl font-black text-emerald-600">
                                                {(priceSummary.estimatedTotal || 0).toLocaleString('en-US', { style: 'currency', currency: 'USD' })}
                                            </span>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}
					</div>
				)}
			</div>
		</div>
	);
};


export default OrderPricingSummary;
